const normalizeMode = (iface) => String(iface?.mode ?? iface?.Mode ?? '').trim().toLowerCase();

// jnet2 reports "station"; EdgeCore reports "sta".
const isStationMode = (iface) => {
  const mode = normalizeMode(iface);
  return mode === 'station' || mode === 'sta';
};

export const getIfaceCounters = (iface) => {
  const c = iface?.counters || iface?.Counters || {};
  return {
    rx_bytes: Number(c.rx_bytes ?? c.RxBytes ?? 0) || 0,
    tx_bytes: Number(c.tx_bytes ?? c.TxBytes ?? 0) || 0,
  };
};

export const sumIfaceCounters = (ifaces) => (
  (ifaces || []).reduce((acc, iface) => {
    const c = getIfaceCounters(iface);
    acc.rx_bytes += c.rx_bytes;
    acc.tx_bytes += c.tx_bytes;
    return acc;
  }, { rx_bytes: 0, tx_bytes: 0 })
);

export const sumEthernetCounters = (ethernetIfaces) => (
  (ethernetIfaces || []).reduce((acc, eth) => {
    const c = eth?.counters || eth?.Counters || {};
    acc.rx_bytes += Number(c.rx_bytes ?? c.RxBytes ?? 0) || 0;
    acc.tx_bytes += Number(c.tx_bytes ?? c.TxBytes ?? 0) || 0;
    return acc;
  }, { rx_bytes: 0, tx_bytes: 0 })
);

export const bytesToMB = (bytes) => {
  const n = Number(bytes) || 0;
  return (n / (1024 * 1024)).toFixed(1);
};

export const formatRateMbps = (bytesPerSec) => {
  const n = Number(bytesPerSec) || 0;
  if (n <= 0) return '0.00';
  // Networking convention: megabits per second (decimal), 1 Mb = 1,000,000 bits.
  return ((n * 8) / 1_000_000).toFixed(2);
};

/** @deprecated use formatRateMbps */
export const formatRateMBps = formatRateMbps;

export const getTrafficRate = (rates, key) => {
  if (!rates) return { rx_bytes_per_sec: 0, tx_bytes_per_sec: 0 };
  const keyMap = {
    total: ['total', 'Total'],
    public_wifi: ['public_wifi', 'PublicWifi'],
    children: ['children', 'Children'],
    ethernet: ['ethernet', 'Ethernet'],
  };
  let block = null;
  for (const candidate of (keyMap[key] || [key])) {
    if (rates[candidate]) {
      block = rates[candidate];
      break;
    }
  }
  if (!block) return { rx_bytes_per_sec: 0, tx_bytes_per_sec: 0 };
  return {
    rx_bytes_per_sec: Number(block.rx_bytes_per_sec ?? block.RxBytesPerSec ?? 0) || 0,
    tx_bytes_per_sec: Number(block.tx_bytes_per_sec ?? block.TxBytesPerSec ?? 0) || 0,
  };
};

export const getDataCounterRates = (device) => (
  device?.data_counter_rates || device?.DataCounterRates || null
);

export const isCapDevice = (device, treeMgmtView) => (
  treeMgmtView?.device_type === 'cap'
  || device?.capabilities?.model === 'JuganuCAP'
);

export const computeDeviceDataCounters = (device, { isCap = false } = {}) => {
  const privateIfaces = device?.private_wifi?.interfaces || device?.private_wifi?.Interfaces || [];
  const publicIfaces = device?.public_wifi?.interfaces || device?.public_wifi?.Interfaces || [];
  const ethernetIfaces = device?.ethernet || device?.Ethernet || [];

  if (isCap) {
    const apIfaces = [...privateIfaces, ...publicIfaces].filter((iface) => normalizeMode(iface) === 'ap');
    return {
      kind: 'cap',
      total: sumIfaceCounters(apIfaces),
      breakdown: {
        public: sumIfaceCounters(publicIfaces),
        children: sumIfaceCounters(privateIfaces.filter((iface) => normalizeMode(iface) === 'ap')),
      },
    };
  }

  const stationIface = privateIfaces.find((iface) => isStationMode(iface));

  return {
    kind: 'node',
    total: stationIface ? getIfaceCounters(stationIface) : { rx_bytes: 0, tx_bytes: 0 },
    breakdown: {
      public: sumIfaceCounters(publicIfaces),
      children: sumIfaceCounters(privateIfaces.filter((iface) => normalizeMode(iface) === 'ap')),
    },
    ethernet: sumEthernetCounters(ethernetIfaces),
  };
};

export const hasDataCounterValues = (counters) => {
  if (!counters) return false;
  const { total, breakdown, ethernet } = counters;
  const parts = [total, breakdown?.public, breakdown?.children, ethernet].filter(Boolean);
  return parts.some((part) => (part.rx_bytes || 0) > 0 || (part.tx_bytes || 0) > 0);
};

export const hasDataCounterRates = (rates) => {
  if (!rates) return false;
  const interval = Number(rates.interval_sec ?? rates.IntervalSec ?? 0);
  return interval > 0;
};

export const getDataCountersSummary = (device) => (
  device?.data_counters || device?.DataCounters || null
);

const summaryCategoryBytes = (summary, key) => {
  if (!summary) return { rx_bytes: 0, tx_bytes: 0 };
  const keyMap = {
    total: ['total', 'Total'],
    public_wifi: ['public_wifi', 'PublicWifi'],
    children: ['children', 'Children'],
    ethernet: ['ethernet', 'Ethernet'],
  };
  let block = null;
  for (const candidate of (keyMap[key] || [key])) {
    if (summary[candidate]) {
      block = summary[candidate];
      break;
    }
  }
  if (!block) return { rx_bytes: 0, tx_bytes: 0 };
  return {
    rx_bytes: Number(block.rx_bytes ?? block.RxBytes ?? 0) || 0,
    tx_bytes: Number(block.tx_bytes ?? block.TxBytes ?? 0) || 0,
  };
};

// Map-layer thresholds for total throughput (RX+TX) in megabits per second (Mb/s).
// Cool blue → purple gradient — avoids red/orange which imply faults on the map.
export const TRAFFIC_RATE_LEGEND = [
  { color: '#9e9e9e', label: 'No data', maxMbps: 0 },
  { color: '#81d4fa', label: '< 4 Mb/s', maxMbps: 4 },
  { color: '#29b6f6', label: '4 – 40 Mb/s', maxMbps: 40 },
  { color: '#1565c0', label: '40 – 200 Mb/s', maxMbps: 200 },
  { color: '#4527a0', label: '> 200 Mb/s', maxMbps: Infinity },
];

const colorForMbps = (mbps) => {
  if (!(mbps > 0)) return TRAFFIC_RATE_LEGEND[0].color;
  if (mbps < 4) return TRAFFIC_RATE_LEGEND[1].color;
  if (mbps < 40) return TRAFFIC_RATE_LEGEND[2].color;
  if (mbps < 200) return TRAFFIC_RATE_LEGEND[3].color;
  return TRAFFIC_RATE_LEGEND[4].color;
};

const bytesPerSecToMbps = (bytesPerSec) => ((Number(bytesPerSec) || 0) * 8) / 1_000_000;

export const TRAFFIC_LAYER_CATEGORIES = [
  { id: 'total', label: 'Total' },
  { id: 'public_wifi', label: 'Public WiFi' },
  { id: 'children', label: 'Children' },
];

export const getTrafficLayerCategoryLabel = (category) => (
  TRAFFIC_LAYER_CATEGORIES.find((c) => c.id === category)?.label || 'Total'
);

// Throughput (RX+TX) in Mb/s for a traffic category. Returns null when no baseline.
export const getCategoryThroughputMbps = (device, category = 'total') => {
  const rates = getDataCounterRates(device);
  if (!hasDataCounterRates(rates)) return null;
  const block = getTrafficRate(rates, category);
  const bytesPerSec = (block.rx_bytes_per_sec || 0) + (block.tx_bytes_per_sec || 0);
  return bytesPerSecToMbps(bytesPerSec);
};

// Cumulative traffic (RX+TX) in MB for a traffic category.
export const getCategoryTrafficMB = (device, category = 'total') => {
  const summary = getDataCountersSummary(device);
  if (!summary) return null;
  const block = summaryCategoryBytes(summary, category);
  return ((block.rx_bytes || 0) + (block.tx_bytes || 0)) / (1024 * 1024);
};

// Total throughput (RX+TX) in Mb/s from persisted data_counter_rates. Returns
// null when no rate baseline is available yet.
export const getDeviceTotalThroughputMbps = (device) => (
  getCategoryThroughputMbps(device, 'total')
);

/** @deprecated use getDeviceTotalThroughputMbps */
export const getDeviceTotalThroughputMBps = getDeviceTotalThroughputMbps;

// Cumulative total traffic (RX+TX) in MB from persisted data_counters.
export const getDeviceTotalTrafficMB = (device) => {
  const summary = getDataCountersSummary(device);
  if (!summary) return null;
  const total = summaryCategoryBytes(summary, 'total');
  return ((total.rx_bytes || 0) + (total.tx_bytes || 0)) / (1024 * 1024);
};

// Returns a marker color for the map traffic layer, or null when the device
// has no usable throughput data (caller falls back to the default marker).
export const getDeviceTrafficColor = (device, category = 'total') => {
  const mbps = getCategoryThroughputMbps(device, category);
  if (mbps == null) return null;
  return colorForMbps(mbps);
};
